// Ildstøtte-komponist (kill-chain): velg våpen + oppdragstype + kontroll, skriv
// målbeskrivelse, plasser målet på kartet → CFF-brief + fareavstands-ring +
// automatisk DANGER CLOSE-sjekk mot egne styrker. Samme flytende panel-mønster
// som stridsmelding-komponisten.
import {
  FIRE_WEAPON_LABEL, THREAT_SYSTEMS, type FireWeapon, type FireMissionType, type FireControl,
} from '@skade/contracts';

export interface FireParams {
  weapon: FireWeapon;
  missionType: FireMissionType;
  control: FireControl;
  targetDesc: string;
  ammunition: string;
}

export type FireSubmit = (p: FireParams) => void;
export type WezSubmit = (threatId: string) => void;

const WEAPONS: FireWeapon[] = ['artillery', 'mortar', 'naval-gunfire', 'rockets', 'cas'];
const MISSIONS: Array<{ id: FireMissionType; label: string }> = [
  { id: 'adjust-fire', label: 'Juster ild' },
  { id: 'fire-for-effect', label: 'Ild for effekt' },
  { id: 'suppress', label: 'Nøytraliser' },
  { id: 'immediate-suppression', label: 'Øyeblikkelig' },
];
const CONTROLS: Array<{ id: FireControl; label: string }> = [
  { id: 'when-ready', label: 'Når klar' },
  { id: 'at-my-command', label: 'På min kommando' },
  { id: 'time-on-target', label: 'Tid på mål' },
  { id: 'continuous', label: 'Kontinuerlig' },
];

export class FireSupportComposer {
  private el: HTMLElement;
  private open = false;

  constructor(private onFire: FireSubmit, private onWez: WezSubmit) {
    this.el = this.build();
    document.body.appendChild(this.el);
  }

  toggle() { this.open ? this.close() : this.show(); }
  private show() { this.open = true; this.el.classList.add('open'); }
  close() { this.open = false; this.el.classList.remove('open'); }

  private build(): HTMLElement {
    const p = div('report-composer firesupport-composer');
    const head = div('report-head');
    head.append(span('report-title', 'Ildstøtte / kill-chain'));
    const x = document.createElement('button');
    x.className = 'report-x'; x.textContent = '×'; x.onclick = () => this.close();
    head.append(x);
    p.append(head);

    const form = div('report-form');

    const weaponSel = labeledSelect(form, 'Våpen', WEAPONS.map((w) => ({ v: w, t: FIRE_WEAPON_LABEL[w] })));
    const missionSel = labeledSelect(form, 'Oppdrag', MISSIONS.map((m) => ({ v: m.id, t: m.label })));
    const controlSel = labeledSelect(form, 'Kontroll', CONTROLS.map((c) => ({ v: c.id, t: c.label })));

    const descWrap = div('report-field');
    descWrap.append(span('report-field-label', 'Målbeskrivelse'));
    const desc = document.createElement('textarea');
    desc.className = 'report-input'; desc.placeholder = 'F.eks. infanteri i skogkant, 2 kjøretøy';
    descWrap.append(desc); form.append(descWrap);

    const ammoWrap = div('report-field');
    ammoWrap.append(span('report-field-label', 'Ammunisjon / effekt'));
    const ammo = document.createElement('input');
    ammo.className = 'report-input'; ammo.value = 'HE';
    ammoWrap.append(ammo); form.append(ammoWrap);

    const submit = document.createElement('button');
    submit.className = 'report-submit';
    submit.textContent = 'Velg mål på kartet';
    submit.onclick = () => {
      this.onFire({
        weapon: weaponSel.value as FireWeapon,
        missionType: missionSel.value as FireMissionType,
        control: controlSel.value as FireControl,
        targetDesc: desc.value.trim() || 'Ukjent mål',
        ammunition: ammo.value.trim() || 'HE',
      });
      this.close();
    };
    form.append(submit);

    // WEZ / trussel-ring
    const wezWrap = div('report-field');
    wezWrap.append(span('report-field-label', 'Trussel-ring (WEZ)'));
    const wezSel = document.createElement('select');
    wezSel.className = 'checklist-sel';
    for (const s of THREAT_SYSTEMS) {
      const o = document.createElement('option');
      o.value = s.id; o.textContent = `${s.name} (${(s.rangeM / 1000).toFixed(0)} km)`;
      wezSel.append(o);
    }
    wezWrap.append(wezSel);
    const wezBtn = document.createElement('button');
    wezBtn.className = 'report-submit'; wezBtn.style.marginTop = '6px';
    wezBtn.textContent = 'Plasser trussel-ring på kartet';
    wezBtn.onclick = () => { this.onWez(wezSel.value); this.close(); };
    wezWrap.append(wezBtn);
    form.append(wezWrap);

    p.append(form);
    return p;
  }
}

function div(cls: string): HTMLElement { const e = document.createElement('div'); e.className = cls; return e; }
function span(cls: string, text: string): HTMLElement { const e = document.createElement('span'); e.className = cls; e.textContent = text; return e; }
function labeledSelect(parent: HTMLElement, label: string, opts: { v: string; t: string }[]): HTMLSelectElement {
  const wrap = div('report-field');
  wrap.append(span('report-field-label', label));
  const s = document.createElement('select');
  s.className = 'checklist-sel';
  for (const o of opts) { const opt = document.createElement('option'); opt.value = o.v; opt.textContent = o.t; s.append(opt); }
  wrap.append(s); parent.append(wrap);
  return s;
}
